import React, { useState, useEffect } from 'react';
import { formatISK } from '@/utils/formatISK';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Check, ChevronDown, ArrowRight, Briefcase, Clock, Camera, FileText, BarChart3, Zap, Users, Receipt, Image, Shield, MapPin, Database, Calculator, HelpCircle, Plus, Minus } from 'lucide-react';
import LandingDemo from '@/components/LandingDemo';

// ── Real app screenshots ──
const IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_0f269d63-5bcc-4ddd-8a5e-d9d9b3c3eb10/artifacts/6qnbwoe1_IMG_3280.PNG',
  scan: 'https://customer-assets.emergentagent.com/job_0f269d63-5bcc-4ddd-8a5e-d9d9b3c3eb10/artifacts/cyl33jwy_IMG_3282.PNG',
  project: 'https://customer-assets.emergentagent.com/job_0f269d63-5bcc-4ddd-8a5e-d9d9b3c3eb10/artifacts/d62q5et1_IMG_3283.PNG',
  time: 'https://customer-assets.emergentagent.com/job_0f269d63-5bcc-4ddd-8a5e-d9d9b3c3eb10/artifacts/l5u8ptws_IMG_3281.PNG',
  export: 'https://customer-assets.emergentagent.com/job_0f269d63-5bcc-4ddd-8a5e-d9d9b3c3eb10/artifacts/ol6t2kb5_IMG_3284.PNG',
};
// Per-language feature images (IS has 5 features, EN has 4)
const FEATURE_IMAGES = {
  // IS order (iter321): project profitability, billing, time tracking, receipts, bookkeeper
  is: [IMAGES.project, IMAGES.export, IMAGES.time, IMAGES.scan, IMAGES.export],
  en: [IMAGES.project, IMAGES.export, IMAGES.time, IMAGES.scan],
};

// ── Translations ──
const T = {
  is: {
    nav: { login: 'Innskráning', trial: 'Skráðu þig' },
    hero: {
      brand: 'REKTRA',
      subtitle: 'Öll verkin þín. Allur kostnaðurinn. Á einum stað.',
      subtitlePrefix: 'Öll verkin þín. Allur kostnaðurinn.',
      subtitleAccent: 'Á einum stað.',
      desc: 'Starfsmenn skrá tíma og kvittanir í símanum. Þú sérð kostnaðinn safnast upp á hverju verki, hvað er tilbúið til að rukka og hvort verkið er í plús eða mínus.',
      tagline: 'Byggt af verktaka fyrir íslenska verktaka.',
      cta: 'Prófaðu frítt í 14 daga',
      cta2: 'Sjá sýnidæmi',
      ctaSub: 'Engin binding. Ekkert kort.',
    },
    trades: {
      h2: 'Fyrir hverja er Rektra?',
      intro: 'Fyrir sjálfstæða verktaka og fyrirtæki sem vilja eyða minni tíma í pappírsvinnu.',
      list: ['Rafvirkja', 'Pípulagningamenn', 'Smiði', 'Múrara', 'Málara', 'Verktakafyrirtæki'],
    },
    trust: {
      h2: 'Íslenskt kerfi fyrir íslenska verktaka',
      items: [
        { icon: 'map', h: 'Byggt af fólki sem þekkir bransann', t: 'Rektra var hannað af fólki sem vinnur sjálft í iðnaðinum og skilur daglegt vesen á verkstað.' },
        { icon: 'database', h: 'Gögnin þín eru þín', t: 'Þú getur flutt út öll gögn hvenær sem er. Engin binding við kerfið.' },
      ],
    },
    pain: {
      h2: 'Kannast þú við þetta?',
      story: 'Tímarnir eru á miða í bílnum.\n\nEfnið var keypt á þremur stöðum.\n\nSvo kemur að því að rukka.\n\nHvað var aftur unnið mikið?\n\nÞú giskar. Og gleymir alltaf einhverju.',
      cards: [
        { icon: 'receipt', h: 'Taktu mynd og gleymdu kvittuninni', t: 'Rektra les kvittunina sjálfkrafa og skráir verslun, upphæð, VSK og dagsetningu.' },
        { icon: 'clock', h: 'Tímaskráning', t: 'Starfsmenn skrá tímana sína beint í appinu. Þú sérð allt á einum stað.' },
        { icon: 'chart', h: 'Bókarinn fær allt tilbúið', t: 'Í lok mánaðar eru gögnin tilbúin til afhendingar. Engin handavinna. Ekkert vesen.' },
      ],
    },
    workflow: {
      h2: 'Svona nota verktakar Rektra á hverjum degi',
      steps: [
        { time: 'Morgun', icon: 'briefcase', h: 'Sjáðu verkefni dagsins', t: 'Starfsmaður opnar appið og sér strax hvað þarf að gera.' },
        { time: 'Á verkstað', icon: 'camera', h: 'Taktu mynd af kvittun', t: 'Kvittunin skráist sjálfkrafa á rétt verkefni.' },
        { time: 'Í lok dags', icon: 'clock', h: 'Skráðu tímana', t: 'Tveir smellir og verkið er búið.' },
        { time: 'Í lok mánaðar', icon: 'file', h: 'Sendu bókaranum gögnin', t: 'Allt tilbúið á einum stað.' },
      ],
    },
    demo: {
      h2: 'Sjáðu hvernig þetta virkar',
      sub: 'Sýnidæmi um hvernig verktakar nota Rektra í daglegum rekstri.',
      cta: 'Smelltu á verk og flettu á milli flipa',
      tabs: ['Verkefni', 'Kvittanir', 'Tímar', 'Fjármál'],
    },
    features: {
      items: [
        { h: 'Veistu hvort verkefnið sé í plús eða mínus', t: 'Allar kvittanir og vinnustundir tengjast verkefninu.\n\nÞú sérð strax hvað verkið hefur kostað.' },
        { h: 'Allt sem þarf til að reikningsfæra', t: 'Rektra heldur utan um tíma, efni og kostnað.\n\nÞú sérð strax hvað er tilbúið til reikningagerðar.' },
        { h: 'Tímaskráningar', t: 'Starfsmenn skrá tíma beint í appinu.\n\nÞú sérð alla tíma á einum stað.' },
        { h: 'Taktu mynd og kvittunin er skráð', t: 'Rektra les verslun, kennitölu seljanda, upphæð, VSK og dagsetningu og skráir á rétta verkið.\n\nFrumritið geymir þú eins og bókhaldslög gera ráð fyrir.' },
        { h: 'Bókarinn fær allt tilbúið', t: 'Í lok mánaðar eru gögnin tilbúin.\n\nÞú getur sent bókaranum eða gefið honum aðgang.' },
      ],
    },
    pricing: {
      h2: 'Hvað kostar Rektra?',
      sub: 'Einfalt verð. Enginn falinn kostnaður.',
      note: 'Skráðu þig á nokkrum mínútum.\nHægt að segja upp hvenær sem er.',
      plans: [
        { name: 'Einstaklingur', price: 7990, desc: 'Fyrir sjálfstæða verktaka', badge: null, features: ['1 notandi', 'Ótakmarkaðar kvittanir', 'Verkefnastjórnun', 'Tímaskráning', 'Tilboðsgerð'] },
        { name: 'Teymið', price: 12990, desc: 'Fyrir fyrirtæki með starfsmenn', badge: 'Mest valið', features: ['Allt í Einstaklingspakka', 'Allt að 5 notendur', 'Tímayfirlit starfsmanna', 'Kostnaðaryfirlit verkefna'] },
        { name: 'Stærri fyrirtæki', price: 24990, desc: 'Fyrir fyrirtæki með mörg verkefni og fleiri starfsmenn', badge: null, features: ['Allt í Teymispakka', 'Allt að 15 notendur', 'Fjölverkefnaskýrslur', 'Forgangsþjónusta'] },
      ],
    },
    roi: {
      h2: 'Hvað kostar að gleyma að rukka?',
      sub: 'Færðu sleðann og sjáðu hvað gleymdar vinnustundir kosta á ári.',
      body: '',
      hoursLabel: 'Klukkustundir á viku sem gleymist að rukka',
      rateLabel: 'Tímagjaldið þitt',
      saved: 'Tapaðar tekjur á ári',
      hoursUnit: 'klst/viku',
      ratePer: 'kr/klst',
      weeksLabel: 'vikur',
      hoursShort: 'klst',
      priceNote: 'Rektra kostar frá 95.880 kr á ári.',
      cta: 'Prófaðu frítt í 14 daga',
    },
    compare: {
      h2: 'Excel vs Rektra',
      sub: 'Allt sem þú þarft. Ekkert vesen.',
      items: [
        'Greining og lestur kvittana',
        'Sjálfvirkur VSK útreikningur',
        'Tímaskráning starfsmanna',
        'Kostnaður á verkefni í rauntíma',
        'Tilboð og reikningar',
        'Útflutningur fyrir bókara',
        'Virkar í síma',
        'Engar týndar kvittanir',
      ],
    },
    faq: {
      h2: 'Spurt og svarað',
      sub: '',
      items: [
        { q: 'Hvernig greiði ég fyrir Rektra?', a: 'Þú færð reikning mánaðarlega og kröfu í heimabankann. Engar kortaupplýsingar.' },
        { q: 'Kostar eitthvað að prófa?', a: 'Nei. Fyrstu 14 dagarnir eru fríir og þú getur sagt upp hvenær sem er á prufutímanum án þess að borga neitt.' },
        { q: 'Get ég bætt starfsmönnum við?', a: 'Já. Teymið er fyrir allt að 5 notendur og Stærri fyrirtæki fyrir allt að 15. Starfsmenn skrá tímana sína beint í símanum.' },
        { q: 'Get ég fært gögnin yfir í bókhaldskerfið?', a: 'Já. Rektra býr til skrár sem bókarinn þinn getur flutt beint inn í bókhaldskerfið. Við bjóðum skráarsnið fyrir DK, Payday, Uniconta og Business Central, auk almenns CSV og Excel.' },
        { q: 'Hvar eru gögnin geymd?', a: 'Gögnin þín eru aðeins aðgengileg þér og þeim sem þú gefur aðgang. Þú getur alltaf sótt gögnin þín út úr Rektra.' },
        { q: 'Get ég gefið bókara aðgang?', a: 'Já. Bókarar geta fengið sinn eigin aðgang.' },
        { q: 'Virkar Rektra í síma?', a: 'Já. Rektra virkar bæði á iPhone og Android.' },
        { q: 'Get ég sagt upp hvenær sem er?', a: 'Já. Engin binding og engin uppsagnargjöld.' },
      ],
    },
    sticky: {
      cta: 'Prófaðu frítt í 14 daga',
    },
    cta: {
      h2: 'Tilbúinn að eyða minni tíma í pappírsvinnu?',
      sub: 'Haltu utan um verkefni, kvittanir og tíma á einum stað.\n\nSendu bókaranum allt tilbúið í lok mánaðar.',
      btn: 'Prófaðu frítt í 14 daga',
    },
    footer: {
      tagline: 'Hannað fyrir íslenska verktaka.',
      links: { pricing: 'Verðskrá', terms: 'Skilmálar', privacy: 'Persónuvernd', contact: 'Hafa samband' },
      copy: '© 2026 Rektra. Öll réttindi áskilin.',
    },
    unit: 'kr / mán',
    startFree: 'Prófaðu frítt í 14 daga',
  },
  en: {
    nav: { login: 'Log in', trial: 'Sign up' },
    hero: {
      brand: 'REKTRA',
      subtitle: 'Stop chasing receipts, hours and paperwork.',
      subtitlePrefix: 'Stop chasing',
      subtitleAccent: 'receipts, hours and paperwork.',
      desc: 'Everything a contractor needs to manage projects, employees and costs in one place.',
      tagline: 'Built by a contractor for Icelandic contractors.',
      cta: 'Try it free for 14 days',
      cta2: 'See a demo',
      ctaSub: 'No commitment. No card.',
    },
    trades: {
      h2: 'Who is Rektra for?',
      intro: 'For independent contractors and companies who want to spend less time on paperwork.',
      list: ['Electricians', 'Plumbers', 'Carpenters', 'Masons', 'Painters', 'Contracting companies'],
    },
    trust: {
      h2: 'An Icelandic system for Icelandic contractors',
      items: [
        { icon: 'map', h: 'Built by people who know the trade', t: 'Rektra was designed by people who work in the industry themselves and understand the daily hassle on a jobsite.' },
        { icon: 'database', h: 'Your data is yours', t: 'You can export all your data at any time. No lock-in.' },
      ],
    },
    pain: {
      h2: 'Sound familiar?',
      story: 'One receipt is in the truck.\n\nAnother in your pocket.\n\nA third on your phone.\n\nThen month-end comes.\n\nYour bookkeeper asks for the data.\n\nAnd you spend half your Sunday hunting it all down.',
      cards: [
        { icon: 'receipt', h: 'Snap it and forget about the receipt', t: 'Rektra reads the receipt automatically and records store, amount, VAT and date.' },
        { icon: 'clock', h: 'Time tracking', t: 'Employees log their hours straight in the app. You see everything in one place.' },
        { icon: 'chart', h: 'Bookkeeper gets it all', t: 'At the end of the month the data is ready to hand over. No manual work. No headaches.' },
      ],
    },
    workflow: {
      h2: 'How contractors use Rektra every day',
      steps: [
        { time: 'Morning', icon: 'briefcase', h: 'See today\u2019s tasks', t: 'Open the app and instantly know what needs to be done.' },
        { time: 'On site', icon: 'camera', h: 'Snap a receipt', t: 'The receipt is filed automatically to the right project.' },
        { time: 'End of day', icon: 'clock', h: 'Log your hours', t: 'Two taps and the job is done.' },
        { time: 'End of month', icon: 'file', h: 'Send the bookkeeper the data', t: 'Everything ready in one place.' },
      ],
    },
    demo: {
      h2: 'See how it works',
      sub: 'A sample of how contractors use Rektra day-to-day.',
      cta: 'Tap a project and switch between tabs',
      tabs: ['Projects', 'Receipts', 'Hours', 'Finance'],
    },
    features: {
      items: [
        { h: 'Know if a job is in the black or red', t: 'All receipts and hours are linked to the project.\n\nYou see right away what the job has cost.' },
        { h: 'Everything you need to invoice', t: 'Rektra tracks hours, materials and costs.\n\nYou see right away what is ready to invoice.' },
        { h: 'Time tracking', t: 'Employees log hours straight in the app.\n\nYou see all hours in one place.' },
        { h: 'Snap it and the receipt is filed', t: 'Rektra reads the store, the supplier kennitala, the amount, the VAT and the date, and files it to the right project.\n\nYou keep the original as the bookkeeping act requires.' },
        { h: 'Bookkeeper gets it all', t: 'At month-end the data is ready.\n\nYou can send it to your bookkeeper or give them access.' },
      ],
    },
    pricing: {
      h2: 'What does Rektra cost?',
      sub: 'Simple pricing. No hidden fees.',
      note: 'Sign up in a few minutes.\nCancel any time.',
      plans: [
        { name: 'Solo', price: 7990, desc: 'For independent contractors', badge: null, features: ['1 user', 'Unlimited receipts', 'Project management', 'Time tracking', 'Quotes'] },
        { name: 'Team', price: 12990, desc: 'For companies with employees', badge: 'Most popular', features: ['Everything in Solo', 'Up to 5 users', 'Employee time overview', 'Project cost overview'] },
        { name: 'Larger companies', price: 24990, desc: 'For companies with many projects and employees', badge: null, features: ['Everything in Team', 'Up to 15 users', 'Multi-project reports', 'Priority support'] },
      ],
    },
    roi: {
      h2: 'What does forgetting to invoice cost?',
      sub: 'Move the slider and see what forgotten billable hours cost per year.',
      body: '',
      hoursLabel: 'Hours per week you forget to invoice',
      rateLabel: 'Your hourly rate',
      saved: 'Lost revenue per year',
      hoursUnit: 'hrs/week',
      ratePer: 'kr/hr',
      weeksLabel: 'weeks',
      hoursShort: 'hrs',
      priceNote: 'Rektra costs from 95,880 kr per year.',
      cta: 'Try it free for 14 days',
    },
    compare: {
      h2: 'Excel vs Rektra',
      sub: 'Everything you need. No hassle.',
      items: [
        'Receipt parsing and OCR',
        'Automatic VAT calculation',
        'Employee time tracking',
        'Real-time cost per project',
        'Quotes and invoices',
        'Bookkeeper export',
        'Works on your phone',
        'Material takeoff and price estimates',
        'No lost receipts',
      ],
    },
    faq: {
      h2: 'Frequently asked',
      sub: '',
      items: [
        { q: 'How do I pay for Rektra?', a: 'You get a monthly invoice and a claim in your online bank. No card details.' },
        { q: 'Does it cost anything to try?', a: 'No. The first 14 days are free and you can cancel at any time during the trial without paying anything.' },
        { q: 'Can I add employees?', a: 'Yes. Team covers up to 5 users and Larger companies up to 15. Employees log their hours straight from their phone.' },
        { q: 'Can I move the data into my accounting system?', a: 'Yes. Rektra produces files your bookkeeper can import straight into the accounting system. We provide formats for DK, Payday, Uniconta and Business Central, plus generic CSV and Excel.' },
        { q: 'Where is my data stored?', a: 'Your data is only accessible to you and to the people you give access. You can always export your data out of Rektra.' },
        { q: 'Can I give my bookkeeper access?', a: 'Yes. Bookkeepers can have their own login.' },
        { q: 'Does Rektra work on mobile?', a: 'Yes. Rektra works on both iPhone and Android.' },
        { q: 'Can I cancel anytime?', a: 'Yes. No commitment and no cancellation fees.' },
      ],
    },
    sticky: {
      cta: 'Try it free for 14 days',
    },
    cta: {
      h2: 'Ready to spend less time on paperwork?',
      sub: 'Keep your projects, receipts and hours in one place.\n\nSend your bookkeeper everything ready at month-end.',
      btn: 'Try it free for 14 days',
    },
    footer: {
      tagline: 'Designed for Icelandic contractors.',
      links: { pricing: 'Pricing', terms: 'Terms', privacy: 'Privacy', contact: 'Contact' },
      copy: '© 2026 Rektra. All rights reserved.',
    },
    unit: 'kr / month',
    startFree: 'Try it free for 14 days',
  },
};

// Icelandic grouping is a dot. Some browser builds ship no is-IS locale data
// and fall back to commas, so group explicitly instead of trusting Intl.
const ISK = (n) => Math.round(n || 0).toLocaleString('en-US').replace(/,/g, '.');

// ── Phone frame ──
const PhoneFrame = ({ src, alt, className = '' }) => (
  <div className={`relative ${className}`}>
    <div className="rounded-[2rem] overflow-hidden" style={{ background: '#0B0B0C', border: '3px solid #2A3138', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)' }}>
      <div className="flex justify-center pt-2 pb-1" style={{ background: '#0B0B0C' }}>
        <div className="w-20 h-5 rounded-full" style={{ background: '#1a1a1a' }} />
      </div>
      <img src={src} alt={alt} className="w-full" loading="lazy" style={{ display: 'block' }} />
    </div>
  </div>
);

// ── Fade-in ──
const useFadeIn = (deps = []) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('landing-visible'); observer.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    const timer = setTimeout(() => {
      document.querySelectorAll('.landing-fade:not(.landing-visible)').forEach(el => observer.observe(el));
    }, 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, deps);
};

// ── Animated stat counter (IntersectionObserver + rAF) ──
const StatCounter = ({ value, label, suffix = '', format, durationMs = 2200 }) => {
  const [display, setDisplay] = useState('0');
  const ref = React.useRef(null);
  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(format ? format : `${formatISK(value)}${suffix}`);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        obs.unobserve(node);
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - start) / durationMs);
          // easeOutCubic
          const eased = 1 - Math.pow(1 - t, 3);
          const current = Math.floor(value * eased);
          setDisplay(format && t === 1 ? format : `${formatISK(current)}${suffix}`);
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [value, suffix, format, durationMs]);
  return (
    <div ref={ref} className="text-center landing-fade" data-testid={`stat-counter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-4xl sm:text-5xl lg:text-6xl font-black tabular-nums" style={{ color: '#22C55E', letterSpacing: '-0.03em' }}>
        {display}
      </div>
      <div className="mt-2 text-xs sm:text-sm uppercase tracking-widest" style={{ color: '#8B9DAF' }}>{label}</div>
    </div>
  );
};

// ── Workflow icon ──
const WfIcon = ({ type }) => {
  const cls = "w-5 h-5";
  if (type === 'briefcase') return <Briefcase className={cls} />;
  if (type === 'camera') return <Camera className={cls} />;
  if (type === 'clock') return <Clock className={cls} />;
  return <FileText className={cls} />;
};

// ── Interactive demo ──
// (Moved to /components/LandingDemo.js)

// ── Forgotten billable hours calculator (user-entered inputs only) ──
const WORKING_WEEKS = 46;
const RoiCalculator = ({ t, lang, onCta, ctaLabel }) => {
  const [hours, setHours] = React.useState(2);
  const [rate, setRate] = React.useState(12000);
  const lostRevenue = hours * rate * WORKING_WEEKS;
  // Group explicitly: some browser builds have no is-IS locale data and fall
  // back to comma grouping, which is wrong in Icelandic.
  const nf = { format: (n) => {
    const s = Math.round(n || 0).toLocaleString('en-US');
    return lang === 'is' ? s.replace(/,/g, '.') : s;
  } };
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="roi-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 landing-fade">{t.h2}</h2>
        <p className="text-center mb-10 text-base landing-fade" style={{ color: '#CBD5E1' }}>{t.sub}</p>
        <div
          className="rounded-2xl p-6 sm:p-8 landing-fade"
          style={{ background: '#151A20', border: '1px solid #2A3138' }}
        >
          {/* Hours slider */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <label className="text-sm font-medium" style={{ color: '#CBD5E1' }}>{t.hoursLabel}</label>
              <span className="text-lg font-bold tabular-nums flex-shrink-0" style={{ color: '#22C55E' }}>
                {hours} <span className="text-xs font-normal" style={{ color: '#9CA3AF' }}>{t.hoursUnit}</span>
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="w-full accent-[#22C55E] cursor-pointer"
              data-testid="roi-hours-slider"
              aria-label={t.hoursLabel}
            />
          </div>
          {/* Rate slider */}
          <div className="mb-8">
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <label className="text-sm font-medium" style={{ color: '#CBD5E1' }}>{t.rateLabel}</label>
              <span className="text-lg font-bold tabular-nums flex-shrink-0" style={{ color: '#22C55E' }}>
                {nf.format(rate)} <span className="text-xs font-normal" style={{ color: '#9CA3AF' }}>{t.ratePer}</span>
              </span>
            </div>
            <input
              type="range"
              min="5000"
              max="30000"
              step="500"
              value={rate}
              onChange={(e) => setRate(parseInt(e.target.value, 10))}
              className="w-full accent-[#22C55E] cursor-pointer"
              data-testid="roi-rate-slider"
              aria-label={t.rateLabel}
            />
          </div>
          {/* Result */}
          <div
            className="rounded-xl p-5 sm:p-6 text-center"
            style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
            data-testid="roi-result"
          >
            <div className="text-xs uppercase tracking-widest mb-2" style={{ color: '#CBD5E1' }}>
              {t.saved}
            </div>
            <div
              className="text-4xl sm:text-5xl font-black tabular-nums"
              style={{ color: '#EF4444', letterSpacing: '-0.02em' }}
            >
              {nf.format(lostRevenue)} <span className="text-2xl sm:text-3xl">kr</span>
            </div>
            <div className="text-xs mt-2 font-mono" style={{ color: '#9CA3AF' }} data-testid="roi-formula">
              {hours} {t.hoursShort} x {nf.format(rate)} kr x {WORKING_WEEKS} {t.weeksLabel}
            </div>
          </div>
          <p className="text-center text-sm mt-4" style={{ color: '#CBD5E1' }} data-testid="roi-price-note">
            {t.priceNote}
          </p>
          <div className="text-center mt-6">
            <button
              onClick={onCta}
              className="px-6 py-3 rounded-lg text-base font-semibold transition-all hover:brightness-110"
              style={{ background: '#22C55E', color: '#0B0B0C' }}
              data-testid="roi-cta"
            >
              {t.cta || ctaLabel}
              <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Feature checklist (was Excel vs Rektra comparison; simplified to "What you get") ──
const ComparisonTable = ({ t }) => (
  <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="compare-section">
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 landing-fade">{t.h2}</h2>
      <p className="text-center mb-10 text-sm landing-fade" style={{ color: '#9CA3AF' }}>{t.sub}</p>
      <div
        className="rounded-2xl p-6 sm:p-8 landing-fade"
        style={{ background: '#151A20', border: '1px solid #2A3138' }}
      >
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          {t.items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm sm:text-base"
              data-testid={`compare-item-${i}`}
            >
              <span
                className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full mt-0.5"
                style={{ background: 'rgba(34,197,94,0.15)' }}
              >
                <Check className="w-3.5 h-3.5" style={{ color: '#22C55E' }} />
              </span>
              <span style={{ color: '#F1F5F9' }}>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ── FAQ Accordion ──
const FaqAccordion = ({ t }) => {
  const [open, setOpen] = React.useState(null);
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ background: '#111518' }} data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-2 mb-3 landing-fade">
          <HelpCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
          <h2 className="text-2xl sm:text-3xl font-bold text-center">{t.h2}</h2>
        </div>
        {t.sub && (
          <p className="text-center mb-10 text-sm landing-fade" style={{ color: '#9CA3AF' }}>{t.sub}</p>
        )}
        <div className={`space-y-3 ${t.sub ? '' : 'mt-10'}`}>
          {t.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="rounded-xl overflow-hidden landing-fade"
                style={{ background: '#151A20', border: '1px solid #2A3138', animationDelay: `${i * 40}ms` }}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left transition-colors hover:bg-white/[0.03]"
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                >
                  <span className="text-sm sm:text-base font-medium" style={{ color: '#F1F5F9' }}>{item.q}</span>
                  <span
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: isOpen ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.08)', color: isOpen ? '#22C55E' : '#94A3B8', transition: 'all 200ms' }}
                  >
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── Sticky mobile CTA: always-visible bottom bar after user scrolls past hero ──
const StickyMobileCta = ({ label, onClick }) => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => {
      // Show after 60% of viewport scrolled (~past the hero on mobile)
      setVisible(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div
      className="md:hidden fixed left-0 right-0 z-[55] px-4 transition-all duration-300"
      style={{
        bottom: 'env(safe-area-inset-bottom, 0px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
        transform: visible ? 'translateY(0)' : 'translateY(120%)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      data-testid="sticky-mobile-cta-wrap"
    >
      <button
        onClick={onClick}
        className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-sm font-bold transition-all active:scale-[0.98]"
        style={{
          background: '#22C55E',
          color: '#0B0B0C',
          boxShadow: '0 10px 30px -8px rgba(34,197,94,0.45), 0 4px 12px rgba(0,0,0,0.4)',
        }}
        data-testid="sticky-mobile-cta"
      >
        {label}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default function LandingPage() {
  const [lang, setLang] = useState('is');
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const t = T[lang];

  useFadeIn([lang]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      navigate(storedUser?.role === 'superadmin' ? '/platform' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Iter307: when the landing page mounts with a hash (e.g. /#pricing
  // from the legacy /pricing redirect or from a "Verðskrá" footer link)
  // scroll to the anchor as soon as it exists. Some sections sit inside
  // `landing-fade` opacity animations, so we poll and RE-align a few
  // times after the element is found — content loading above pushes
  // the target's absolute position downward.
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    if (!id) return;
    let tries = 0;
    const scrollNow = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    };
    const loop = () => {
      scrollNow();
      if (tries < 30) {
        tries += 1;
        setTimeout(loop, 100);
      }
    };
    loop();
  }, [loading]);

  // All primary CTAs now route directly to the live signup page so visitors
  // can subscribe immediately. Waitlist flow is retired but kept in the codebase
  // (component + data) for historic records.
  const goRegister = () => navigate('/register');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0B0C' }}>
        <div className="w-8 h-8 border-4 border-[#22C55E]/30 border-t-[#22C55E] rounded-full animate-spin" />
      </div>
    );
  }

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen relative isolate" style={{ background: '#0B0B0C', color: '#F1F5F9', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ── Global grain overlay (SVG noise data URI, very subtle) ── */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[60]"
        style={{
          opacity: 0.04,
          mixBlendMode: 'screen',
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* ── Hero gradient mesh (radial gradients, breathing animation) ── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 overflow-hidden"
        style={{ height: '110vh' }}
      >
        <div
          className="absolute"
          style={{
            top: '-15%', right: '-10%', width: '760px', height: '760px',
            borderRadius: '999px',
            background: 'radial-gradient(circle at center, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.04) 45%, rgba(11,11,12,0) 70%)',
            filter: 'blur(60px)',
            animation: 'rektra-mesh-breathe 9s ease-in-out infinite',
          }}
        />
        <div
          className="absolute"
          style={{
            bottom: '-20%', left: '-15%', width: '620px', height: '620px',
            borderRadius: '999px',
            background: 'radial-gradient(circle at center, rgba(56,189,248,0.10) 0%, rgba(11,11,12,0) 65%)',
            filter: 'blur(80px)',
            animation: 'rektra-mesh-breathe 11s ease-in-out infinite 2s',
          }}
        />
      </div>

      <style>{`
        @keyframes rektra-mesh-breathe {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        @keyframes rektra-hero-ping {
          75%, 100% {
            transform: scale(2.6);
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          [aria-hidden="true"] [style*="rektra-mesh-breathe"] { animation: none !important; }
          [style*="rektra-hero-ping"] { animation: none !important; }
        }
      `}</style>
      {/* ── NAVBAR ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md" style={{ background: 'rgba(11,11,12,0.85)', borderBottom: '1px solid #2A3138' }} data-testid="landing-navbar">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/logos/logo-white.png" alt="REKTRA" className="h-7" data-testid="landing-logo" />
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/verd')} className="hidden sm:inline text-sm font-medium transition-colors hover:text-white" style={{ color: '#9CA3AF' }} data-testid="landing-nav-pricing-link">
              Verð
            </button>
            <div className="flex rounded-md overflow-hidden text-xs font-medium" style={{ border: '1px solid #2A3138' }} data-testid="lang-toggle">
              <button onClick={() => setLang('is')} className="px-2.5 py-1.5 transition-colors" style={{ background: lang === 'is' ? '#22C55E' : 'transparent', color: lang === 'is' ? '#0B0B0C' : '#9CA3AF' }}>IS</button>
              <button onClick={() => setLang('en')} className="px-2.5 py-1.5 transition-colors" style={{ background: lang === 'en' ? '#22C55E' : 'transparent', color: lang === 'en' ? '#0B0B0C' : '#9CA3AF' }}>EN</button>
            </div>
            <button onClick={() => navigate('/login')} className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors hover:brightness-110" style={{ background: '#22C55E', color: '#0B0B0C' }} data-testid="landing-login-btn">
              {t.nav.login}
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO — refined, headline-first ── */}
      <section className="pt-28 pb-16 sm:pt-36 sm:pb-24 px-4 sm:px-6" data-testid="hero-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            {/* Brand lockup: pulsing green dot + REKTRA wordmark */}
            <div
              className="flex items-center justify-center lg:justify-start gap-2.5 mb-3 landing-fade"
              data-testid="hero-brand-lockup"
            >
              <span className="relative flex h-2 w-2">
                <span
                  className="absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ background: '#22C55E', animation: 'rektra-hero-ping 1.8s cubic-bezier(0,0,0.2,1) infinite' }}
                />
                <span
                  className="relative inline-flex h-2 w-2 rounded-full"
                  style={{ background: '#22C55E' }}
                />
              </span>
              <span
                className="font-black text-xl sm:text-2xl"
                style={{ color: '#F1F5F9', letterSpacing: '0.18em' }}
              >
                {t.hero.brand}
              </span>
            </div>
            {/* Green tagline under brand mark */}
            <p
              className="text-[11px] sm:text-xs font-semibold uppercase mb-7 landing-fade"
              style={{ color: '#22C55E', letterSpacing: '0.16em' }}
              data-testid="hero-eyebrow"
            >
              {t.hero.tagline}
            </p>
            {/* Massive H1 — the actual value prop */}
            <h1
              className="font-black leading-[0.95] tracking-tight mb-6 landing-fade"
              style={{
                color: '#F1F5F9',
                fontSize: 'clamp(2.5rem, 8vw, 5.5rem)',
                letterSpacing: '-0.025em',
                textWrap: 'balance',
              }}
              data-testid="hero-headline"
            >
              {t.hero.subtitlePrefix}{' '}
              <span
                className="relative inline"
                style={{
                  color: '#22C55E',
                  backgroundImage: 'linear-gradient(180deg, transparent 72%, rgba(34,197,94,0.18) 72%, rgba(34,197,94,0.18) 96%, transparent 96%)',
                }}
              >
                {t.hero.subtitleAccent}
              </span>
            </h1>
            {/* Supporting paragraph */}
            <p className="text-base sm:text-lg lg:text-xl leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 landing-fade" style={{ color: '#9CA3AF' }}>
              {t.hero.desc}
            </p>
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start landing-fade">
              <button
                onClick={goRegister}
                className="px-7 py-3.5 rounded-xl text-base font-bold transition-all hover:brightness-110 active:scale-[0.98]"
                style={{ background: '#22C55E', color: '#0B0B0C', boxShadow: '0 10px 30px -8px rgba(34,197,94,0.4)' }}
                data-testid="hero-cta-primary"
              >
                {t.hero.cta}
                <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
              <button
                onClick={() => scrollTo('demo')}
                className="px-7 py-3.5 rounded-xl text-base font-semibold transition-colors hover:bg-white/5"
                style={{ border: '1px solid #2A3138', color: '#F1F5F9' }}
                data-testid="hero-cta-secondary"
              >
                {t.hero.cta2}
                <ChevronDown className="inline ml-1 w-4 h-4" />
              </button>
            </div>
            <p className="text-xs mt-5 max-w-sm text-center lg:text-left landing-fade" style={{ color: '#8B9DAF' }}>{t.hero.ctaSub}</p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <PhoneFrame src={IMAGES.hero} alt="Rektra dashboard" className="w-60 sm:w-72" />
          </div>
        </div>
      </section>

      {/* ── TRADES: who it's for ── */}
      <section className="py-12 sm:py-14 px-4" style={{ background: '#111518' }} data-testid="trades-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-lg sm:text-xl font-bold mb-3 landing-fade">{t.trades.h2}</h2>
          <p className="text-sm sm:text-base leading-relaxed mb-7 landing-fade" style={{ color: '#9CA3AF' }}>{t.trades.intro}</p>
          <div className="flex flex-wrap justify-center gap-3 landing-fade">
            {t.trades.list.map((trade, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: '#1A2028', border: '1px solid #2A3138', color: '#CBD5E1' }}>
                {trade}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP — 2 honest pillars ── */}
      <section className="py-14 sm:py-20 px-4" data-testid="trust-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10 landing-fade">{t.trust.h2}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
            {t.trust.items.map((item, i) => {
              const icons = {
                shield: <Shield className="w-5 h-5" />,
                map: <MapPin className="w-5 h-5" />,
                database: <Database className="w-5 h-5" />,
              };
              return (
                <div
                  key={i}
                  className="rounded-xl p-6 sm:p-7 landing-fade"
                  style={{ background: '#151A20', border: '1px solid #2A3138', animationDelay: `${i * 100}ms` }}
                  data-testid={`trust-card-${i}`}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}
                  >
                    {icons[item.icon]}
                  </div>
                  <h3 className="text-base font-semibold mb-2">{item.h}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>{item.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PAIN STORY ── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="pain-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10 landing-fade">{t.pain.h2}</h2>
          {/* Emotional story block */}
          <div className="max-w-lg mx-auto mb-14 landing-fade">
            <div className="rounded-xl p-8 text-center" style={{ background: '#151A20', border: '1px solid #2A3138' }}>
              <p className="text-base sm:text-lg leading-loose italic" style={{ color: '#CBD5E1', whiteSpace: 'pre-line' }}>
                {t.pain.story}
              </p>
            </div>
          </div>
          {/* Solution cards */}
          <div className="grid sm:grid-cols-3 gap-6">
            {t.pain.cards.map((c, i) => {
              const icons = { receipt: <Receipt className="w-6 h-6" />, clock: <Clock className="w-6 h-6" />, chart: <BarChart3 className="w-6 h-6" /> };
              return (
                <div key={i} className="rounded-xl p-6 landing-fade" style={{ background: '#151A20', border: '1px solid #2A3138', animationDelay: `${i * 100}ms` }}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}>
                    {icons[c.icon]}
                  </div>
                  <h3 className="text-base font-semibold mb-2">{c.h}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#9CA3AF' }}>{c.t}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── DAILY WORKFLOW TIMELINE ── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="workflow-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-14 landing-fade">{t.workflow.h2}</h2>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[23px] sm:left-1/2 top-0 bottom-0 w-px" style={{ background: '#2A3138' }} />
            <div className="space-y-16 sm:space-y-20">
              {t.workflow.steps.map((s, i) => (
                <div key={i} className={`flex items-start gap-5 sm:gap-0 landing-fade ${i % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`} style={{ animationDelay: `${i * 150}ms` }}>
                  {/* Mobile: all left-aligned. Desktop: alternating */}
                  <div className={`hidden sm:block sm:w-[calc(50%-24px)] ${i % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>{s.time}</span>
                    <h3 className="text-base font-semibold mt-1">{s.h}</h3>
                    <p className="text-sm mt-1" style={{ color: '#9CA3AF' }}>{s.t}</p>
                  </div>
                  {/* Dot */}
                  <div className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center z-10" style={{ background: '#151A20', border: '2px solid #22C55E' }}>
                    <WfIcon type={s.icon} />
                  </div>
                  {/* Mobile text */}
                  <div className="sm:hidden flex-1">
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>{s.time}</span>
                    <h3 className="text-base font-semibold mt-1">{s.h}</h3>
                    <p className="text-sm mt-1" style={{ color: '#9CA3AF' }}>{s.t}</p>
                  </div>
                  <div className={`hidden sm:block sm:w-[calc(50%-24px)]`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI / SAVINGS CALCULATOR ── */}
      <RoiCalculator t={t.roi} lang={lang} onCta={goRegister} ctaLabel={t.hero.cta} />

      {/* ── INTERACTIVE DEMO ── */}
      <section id="demo" className="py-16 sm:py-24 px-4 sm:px-6" style={{ background: '#111518' }} data-testid="demo-section">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 landing-fade">{t.demo.h2}</h2>
          <p className="text-center mb-2 text-sm landing-fade" style={{ color: '#9CA3AF' }}>{t.demo.sub}</p>
          <p className="text-center mb-8 text-sm font-medium landing-fade flex items-center justify-center gap-1.5" style={{ color: '#CBD5E1' }} data-testid="demo-hint">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 15l-6-6m0 0v4.5m0-4.5h4.5M4 20h16a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            {t.demo.cta}
          </p>
          <LandingDemo lang={lang} />
          <div className="text-center mt-8 landing-fade">
            <button onClick={goRegister} className="px-6 py-3 rounded-lg text-base font-semibold transition-all hover:brightness-110" style={{ background: '#22C55E', color: '#0B0B0C' }} data-testid="demo-cta">
              {t.hero.cta}
              <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ── FEATURES (with phone frames) ── */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          {t.features.items.map((f, i) => {
            const images = FEATURE_IMAGES[lang] || FEATURE_IMAGES.en;
            const imgSrc = images[i];
            return (
            <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 lg:gap-16 items-center mb-20 last:mb-0 landing-fade`}>
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-4">{f.h}</h3>
                <div className="text-base leading-relaxed" style={{ color: '#9CA3AF' }}>
                  {typeof f.t === 'string' ? f.t.split('\n').map((line, j) => {
                    if (line === '') return <div key={j} className="h-3" />;
                    if (line.startsWith('\u2022')) return <div key={j} className="flex gap-2 py-0.5"><span>{'\u2022'}</span><span>{line.slice(2)}</span></div>;
                    return <p key={j}>{line}</p>;
                  }) : f.t}
                </div>
              </div>
              {imgSrc && (
                <div className="flex-shrink-0">
                  <PhoneFrame src={imgSrc} alt={f.h} className="w-48 sm:w-56" />
                </div>
              )}
            </div>
            );
          })}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-16 sm:py-24 px-4 sm:px-6" style={{ background: '#111518' }} data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3 landing-fade">{t.pricing.h2}</h2>
          <p className="text-center mb-12 landing-fade" style={{ color: '#9CA3AF' }}>{t.pricing.sub}</p>
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            {t.pricing.plans.map((plan, i) => {
              const isPopular = i === 1;
              return (
                <div key={i} className="rounded-xl p-6 relative landing-fade flex flex-col" style={{
                  background: '#151A20', border: isPopular ? '2px solid #22C55E' : '1px solid #2A3138',
                  boxShadow: isPopular ? '0 0 30px rgba(34,197,94,0.15)' : 'none', animationDelay: `${i * 100}ms`,
                }}>
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#22C55E', color: '#0B0B0C' }}>{plan.badge}</div>
                  )}
                  <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                  <p className="text-sm mb-4" style={{ color: '#6B7280' }}>{plan.desc}</p>
                  {plan.price && (
                    <div className="mb-6">
                      <span className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{ISK(plan.price)}</span>
                      <span className="text-sm ml-1" style={{ color: '#6B7280' }}>{t.unit}</span>
                    </div>
                  )}
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {plan.features.map((feat, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#22C55E' }} />
                        <span style={{ color: '#9CA3AF' }}>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={goRegister} className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all hover:brightness-110" style={
                    isPopular ? { background: '#22C55E', color: '#0B0B0C' } : { border: '1px solid #2A3138', color: '#F1F5F9' }
                  } data-testid={`pricing-cta-${i}`}>
                    {t.startFree}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-sm whitespace-pre-line" style={{ color: '#6B7280' }}>{t.pricing.note}</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <FaqAccordion t={t.faq} />

      {/* ── FINAL CTA ── */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 landing-fade">{t.cta.h2}</h2>
          <p className="mb-8 landing-fade" style={{ color: '#9CA3AF', whiteSpace: 'pre-line' }}>{t.cta.sub}</p>
          <button onClick={goRegister} className="px-8 py-3.5 rounded-lg text-base font-semibold transition-all hover:brightness-110 landing-fade" style={{ background: '#22C55E', color: '#0B0B0C' }} data-testid="final-cta-btn">
            {t.cta.btn}<ArrowRight className="inline ml-2 w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-4 sm:px-6" style={{ borderTop: '1px solid #2A3138' }} data-testid="landing-footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/logos/logo-white.png" alt="REKTRA" className="h-5" />
            <span className="text-sm" style={{ color: '#6B7280' }}>{t.footer.tagline}</span>
          </div>
          <div className="flex flex-wrap justify-center gap-5 text-sm" style={{ color: '#6B7280' }}>
            <button onClick={() => navigate('/verd')} className="hover:text-white transition-colors" data-testid="landing-footer-pricing-link">{t.footer.links.pricing}</button>
            <a href="/terms" className="hover:text-white transition-colors">{t.footer.links.terms}</a>
            <a href="/privacy" className="hover:text-white transition-colors">{t.footer.links.privacy}</a>
            <a href="/contact" className="hover:text-white transition-colors">{t.footer.links.contact}</a>
          </div>
          <p className="text-xs" style={{ color: '#6B7280' }}>{t.footer.copy}</p>
        </div>
      </footer>

      {/* ── STICKY MOBILE CTA — appears after user scrolls past hero ── */}
      <StickyMobileCta label={t.sticky.cta} onClick={goRegister} />
    </div>
  );
}
